import SVGInject from '@iconfu/svg-inject'
import AOS from 'aos';

const ariaLabelText = document.querySelector("main")?.getAttribute("data-link-popup-aria")?.trim();

/**
 * Append the standard new-window suffix when it isn't already present.
 * @param {string} value - Existing aria-label or title text
 * @returns {string} Updated string including suffix when needed
 */
function appendSuffix(value = '') {
    if (!ariaLabelText) {
        return value;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return ariaLabelText;
    }

    const normalizedSuffix = ariaLabelText.toLowerCase();
    if (trimmedValue.toLowerCase().endsWith(normalizedSuffix)) {
        return trimmedValue;
    }

    return `${trimmedValue} ${ariaLabelText}`;
}

function addAriaLabelToLink(link) {
    const linkText = link.innerText;
    const linkTitle = link.getAttribute("title");
    link.setAttribute("aria-label", appendSuffix(linkText));
    if (linkTitle) {
        link.setAttribute("title", appendSuffix(linkTitle));
    } else {
        link.setAttribute("title", appendSuffix(linkText));
    }
}

function addArialLabelToPdf(link) {
    if (!link.hasAttribute("download")) {
        if (link.hasAttribute("aria-label")) {
            const ariaLabel = link.getAttribute("aria-label");
            link.setAttribute("aria-label", appendSuffix(ariaLabel));
        } else {
            addAriaLabelToLink(link);
        }
    }
}

function removeRecaptchaPresentationalRole() {
    const fixRecaptchaIframe = (observerInstance) => {
        const recaptchaIframe = document.querySelector('iframe[src*="recaptcha/api2/anchor"]');
        
        if (recaptchaIframe) {
            if (recaptchaIframe.getAttribute('role') === 'presentation') {
                recaptchaIframe.removeAttribute('role');
            }
            // Stop observing completely once the iframe is found and fixed
            if (observerInstance) {
                observerInstance.disconnect();
            }
            return true;
        }
        return false;
    };

    // 1. Check immediately if it's already there
    const found = fixRecaptchaIframe();

    // 2. Only spin up the observer if the iframe hasn't loaded yet
    if (!found) {
        const observer = new MutationObserver((mutations, obs) => {
            fixRecaptchaIframe(obs);
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

SVGInject.setOptions({
    onFail: function(img) {
        img.classList.remove('injectable');
    }
});

document.addEventListener('DOMContentLoaded', function() {
    SVGInject(document.querySelectorAll('img.injectable'), {
        onAllFinish: function() {
            // Remove metadata from SVGs
            for (const svg of document.querySelectorAll("svg")) {
                svg.querySelector("metadata")?.remove();
                svg.removeAttribute("id");
                svg.querySelector("g")?.removeAttribute("id");
            }
        }
    });

    AOS.init({ duration: 800, once: true });

    removeRecaptchaPresentationalRole();
});

setTimeout(() => {
    function isDeviceIOS() {
        return [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod'
        ].includes(navigator.platform)
            // iPad on iOS 13 detection
            || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    }

    for (const link of document.querySelectorAll("a")) {
        // Set every pdf file to open in new window
        if (link.getAttribute("href")?.endsWith("pdf")) {
            link.setAttribute("target", "_blank");
            addArialLabelToPdf(link);

            if (isDeviceIOS() && link.closest(".adm-section-c17-download")) {
                link.removeAttribute("download");
            }
        }
        // Add aria-label and title to links that open in new tab
        if (link.getAttribute("target") === "_blank" && !link.hasAttribute("aria-label") && !link.hasAttribute("download")) {
            addAriaLabelToLink(link);
        }
    }

    // Change every <b> tag to <strong> tag
    for (const boldText of document.querySelectorAll("b")) {
        const strongTag = document.createElement("strong");
        for (const attr of boldText.attributes) {
            strongTag.setAttribute(attr.name, attr.value);
        }
        strongTag.innerHTML = boldText.innerHTML;
        boldText.replaceWith(strongTag);
    }
}, 150);

setTimeout(() => {
    // Wrap every ADM custom table in a div
    for (const table of document.querySelectorAll("table.bwtablemarginb")) {
        const tableParent = table.parentNode;
        const tableHTML = table.outerHTML;
        const wrappedTable = document.createElement('div');
        wrappedTable.style["overflow-x"] = "auto";
        wrappedTable.innerHTML = tableHTML;
        tableParent.insertBefore(wrappedTable, table);
        tableParent.removeChild(table);
    }
    for (const table of document.querySelectorAll("table.bwtablebottommargin")) {
        const tableParent = table.parentNode;
        const tableHTML = table.outerHTML;
        const wrappedTable = document.createElement('div');
        wrappedTable.style["overflow-x"] = "auto";
        wrappedTable.innerHTML = tableHTML;
        tableParent.insertBefore(wrappedTable, table);
        tableParent.removeChild(table);
    }

    // Remove custom incompatible class from tables
    for (const parentClass of document.querySelectorAll(".q4default")) {
        parentClass.classList.remove("q4default");
    }
}, 0);

function addNaturalSizeAttrs(img, attrs) {
    for (const attr of attrs) {
        const attrFirstLetterUppercase = attr[0].toUpperCase() + attr.slice(1);
        img.setAttribute(attr, img[`natural${attrFirstLetterUppercase}`]);
    }
}

const allImages = document.querySelectorAll("img");
for (const img of allImages) {
    let attrArray = [];

    if (!img.hasAttribute("width")) attrArray.push("width");
    if (!img.hasAttribute("height")) attrArray.push("height");

    switch (true) {
        case attrArray.length !== 0 && img.naturalHeight !== 0:
            addNaturalSizeAttrs(img, attrArray);
            break;
        case attrArray.length !== 0:
            img.addEventListener("load", () => {
                if (img.naturalHeight !== 0) {
                    addNaturalSizeAttrs(img, attrArray)
                } else {
                    for (const attr of attrArray) {
                        img.setAttribute(attr, "20");
                    }
                }
            });
            break;
    }
}

async function postSchema() {
    const currentContent = document.getElementsByTagName("HTML")[0].getAttribute("data-apinode");
    const reqPath = window.location.origin + "/StructuredData/GenerateSchema?contentLink=" + encodeURIComponent(currentContent);

    try {
        const response = await fetch(reqPath, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;charset=utf-8'
            }
        });

        const htmlString = await response.json();

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString.trim();
        document.querySelector("head").appendChild(tempDiv.firstChild);

    } catch {
        // Error handling
        console.error("Something went wrong");
    }
}
postSchema();

function convertHttpToHttps() {
    var links = document.querySelectorAll("a");
    links.forEach(link => {
        var linkHref = link.href;
        if (linkHref && linkHref.includes("http:") && linkHref.includes("adm.com") && !linkHref.includes("localhost")) {
            link.href = linkHref.replace("http:", "https:");
        }
    });
}

convertHttpToHttps();

// Add title and aria-label to cookies link on footer
const footerCookies = document.querySelector("footer .ot-sdk-show-settings");
if (footerCookies) {
    const footerCookiesObserver = new MutationObserver(function (mutationList) {
        const addedCookiesText = mutationList[0].addedNodes[0].nodeValue;
        footerCookies.setAttribute("title", addedCookiesText);
        footerCookies.setAttribute("aria-label", addedCookiesText);
        footerCookiesObserver.disconnect();
    })
    footerCookiesObserver.observe(footerCookies, { childList: true });
}